import classnames from 'classnames';
import { useState, type Dispatch, type SetStateAction } from 'react';

import { WorkspaceFilters as WorkspaceFiltersType } from '@app/@types/redux/workspace.ts';
import { SORT_DIR } from '@libs/constants/constants';
import { translate } from '@state/utils/helper.ts';
import { LuFolderCheck, LuFolderClock } from 'react-icons/lu';
import { PiFoldersDuotone } from 'react-icons/pi';
import { Nav, NavItem, NavLink } from 'reactstrap';

export const WORKSPACE_SORT_BY = {
  WORKSPACE_NAME: 'workspace_name',
  WORKSPACE_START_DATE: 'workspace_start_date',
  WORKSPACE_END_DATE: 'workspace_end_date',
};

export const PROJECT_SORT_BY = {
  PROJECT_NAME: 'project_name',
  PROJECT_START_DATE: 'project_begin_date',
  PROJECT_SUBMISSION_DEADLINE: 'project_submission_deadline',
  PROJECT_END_DATE: 'project_end_date',
  PROJECT_LAST_ACTIVE: 'updated_at',
};

const PAGE_TYPE = {
  WORKSPACES: 'workspaces',
  ACTIVE_PROJECTS: 'activeProjects',
  FINISHED_PROJECTS: 'finishedProjects',
};

export interface IconBaseProps extends React.SVGAttributes<SVGElement> {
  children?: React.ReactNode;
  size?: string | number;
  color?: string;
  title?: string;
}

export type IconType = (props: IconBaseProps) => JSX.Element;

interface Option {
  value: string;
  label: string;
  sortBy: (typeof WORKSPACE_SORT_BY)[keyof typeof WORKSPACE_SORT_BY];
  sortDirection: (typeof SORT_DIR)[keyof typeof SORT_DIR];
}

/**
 * Renders filtering and sorting controls for workspace and project simulations.
 *
 * This component displays navigation tabs for workspaces, active projects, and finished projects.
 * Depending on the active tab, it conditionally renders a dropdown filter with the appropriate sorting options.
 * Selecting an option updates the corresponding filter state, and choosing "Clear" resets the workspace filters.
 *
 * @param props.tab - The active view identifier (e.g., workspaces, active projects, or finished projects).
 * @param props.setTab - Function to update the active view.
 * @param props.workspaceFilters - Current sorting filter settings for workspaces.
 * @param props.setWorkspaceFilters - Function to update the workspace sorting filters.
 * @param props.projectFilters - Current sorting filter settings for simulations.
 * @param props.setProjectFilters - Function to update the simulation sorting filters.
 */
function WorkspaceFilters(props: {
  tab: string;
  setTab: Dispatch<SetStateAction<string>>;
  workspaceFilters: WorkspaceFiltersType;
  setWorkspaceFilters: Dispatch<SetStateAction<WorkspaceFiltersType>>;
  projectFilters: WorkspaceFiltersType;
  setProjectFilters: Dispatch<SetStateAction<WorkspaceFiltersType>>;
}) {
  const { tab, setTab, workspaceFilters, setWorkspaceFilters, projectFilters, setProjectFilters } =
    props;

  const sortOptions: Option[] = [
    {
      value: 'sort-name-asc',
      label: translate('listWorkspaces.filters.projects.projectNameAsc'),
      sortBy: PROJECT_SORT_BY.PROJECT_NAME,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-name-desc',
      label: translate('listWorkspaces.filters.projects.projectNameDesc'),
      sortBy: PROJECT_SORT_BY.PROJECT_NAME,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'sort-start-date-asc',
      label: translate('listWorkspaces.filters.projects.projectStartDateAsc'),
      sortBy: PROJECT_SORT_BY.PROJECT_START_DATE,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-start-date-desc',
      label: translate('listWorkspaces.filters.projects.projectStartDateDesc'),
      sortBy: PROJECT_SORT_BY.PROJECT_START_DATE,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'sort-last-active-asc',
      label: translate('listWorkspaces.filters.projects.lastActiveAsc'),
      sortBy: PROJECT_SORT_BY.PROJECT_LAST_ACTIVE,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-last-active-desc',
      label: translate('listWorkspaces.filters.projects.lastActiveDesc'),
      sortBy: PROJECT_SORT_BY.PROJECT_LAST_ACTIVE,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'sort-submission-deadline-asc',
      label: translate('listWorkspaces.filters.projects.submissionDeadlineAsc'),
      sortBy: PROJECT_SORT_BY.PROJECT_SUBMISSION_DEADLINE,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-submission-deadline-desc',
      label: translate('listWorkspaces.filters.projects.submissionDeadlineDesc'),
      sortBy: PROJECT_SORT_BY.PROJECT_SUBMISSION_DEADLINE,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'clear',
      label: translate('listWorkspaces.filters.projects.clear'),
      sortBy: '',
      sortDirection: '',
    },
  ];

  const sortOptionsWorkspace: Option[] = [
    {
      value: 'sort-name-asc',
      label: translate('listWorkspaces.filters.workspaces.workspaceNameAsc'),
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_NAME,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-name-desc',
      label: translate('listWorkspaces.filters.workspaces.workspaceNameDesc'),
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_NAME,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'sort-start-date-asc',
      label: translate('listWorkspaces.filters.workspaces.workspaceStartDateAsc'),
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_START_DATE,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-start-date-desc',
      label: translate('listWorkspaces.filters.workspaces.workspaceStartDateDesc'),
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_START_DATE,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'sort-end-date-asc',
      label: translate('listWorkspaces.filters.workspaces.workspaceEndDateAsc'),
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_END_DATE,
      sortDirection: SORT_DIR.ASC,
    },
    {
      value: 'sort-end-date-desc',
      label: translate('listWorkspaces.filters.workspaces.workspaceEndDateDesc'),
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_END_DATE,
      sortDirection: SORT_DIR.DESC,
    },
    {
      value: 'clear',
      label: translate('listWorkspaces.filters.workspaces.clear'),
      sortBy: '',
      sortDirection: '',
    },
  ];

  const [sortSelectedValue, setSortSelectedValue] = useState<Option>(
    sortOptions.find((option) => option.value === projectFilters.sortBy) ?? sortOptions[0]
  );
  const [sortSelectedValueWorkspace, setSortSelectedValueWorkspace] = useState<Option>(
    sortOptionsWorkspace.find((option) => option.value === workspaceFilters.sortBy) ??
      sortOptionsWorkspace[0]
  );

  const resetWorkspaceFiltersFn = () => {
    setWorkspaceFilters({
      sortBy: WORKSPACE_SORT_BY.WORKSPACE_START_DATE,
      sortDirection: SORT_DIR.DESC,
    });
  };

  const handleChange = (selectedOption: Option) => {
    if (selectedOption.value === 'clear') {
      setSortSelectedValue({
        value: '',
        label: 'Sort',
        sortBy: '',
        sortDirection: '',
      });
      setProjectFilters({
        sortBy: PROJECT_SORT_BY.PROJECT_NAME,
        sortDirection: SORT_DIR.ASC,
      });
      return;
    }

    setSortSelectedValue(selectedOption);
    setProjectFilters({
      sortBy: selectedOption.sortBy,
      sortDirection: selectedOption.sortDirection,
    });
  };

  const handleChangeWorkspace = (selectedOption: Option) => {
    if (selectedOption.value === 'clear') {
      resetWorkspaceFiltersFn();
      const defaultOption =
        sortOptionsWorkspace.find(
          (option) =>
            option.sortBy === WORKSPACE_SORT_BY.WORKSPACE_START_DATE &&
            option.sortDirection === SORT_DIR.DESC
        ) ?? sortOptionsWorkspace[0];
      setSortSelectedValueWorkspace(defaultOption);
      return;
    }
    setSortSelectedValueWorkspace(selectedOption);
    setWorkspaceFilters({
      sortBy: selectedOption.sortBy,
      sortDirection: selectedOption.sortDirection,
    });
  };

  return (
    <div>
      <div className="px-1 pb-2 mt-3 d-flex justify-content-start">
        <div className="position-relative justify-content-start d-flex">
          <Nav className=" overviewPageNav1 overviewPageNav" id="tabs-icons-text" role="tablist">
            <NavItem>
              <NavLink
                aria-selected={tab === PAGE_TYPE.WORKSPACES}
                className={classnames('navpill font-weight-500', {
                  active: tab === PAGE_TYPE.WORKSPACES,
                })}
                style={{
                  fontSize: '14px',
                }}
                onClick={() => {
                  setTab(PAGE_TYPE.WORKSPACES);
                }}
                href="#"
                role="tab"
              >
                <PiFoldersDuotone className="mr-2" size={18} />
                {translate('listWorkspaces.myWorkspaces')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                aria-selected={tab === PAGE_TYPE.ACTIVE_PROJECTS}
                className={classnames('navpill mb-sm-3 mb-md-0 font-weight-500', {
                  active: tab === PAGE_TYPE.ACTIVE_PROJECTS,
                })}
                style={{
                  fontSize: '14px',
                }}
                onClick={() => {
                  setTab(PAGE_TYPE.ACTIVE_PROJECTS);
                }}
                href="#"
                role="tab"
              >
                <LuFolderClock className="mr-2" size={18} />
                {translate('listWorkspaces.activeSimulations')}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                aria-selected={tab === PAGE_TYPE.FINISHED_PROJECTS}
                className={classnames('navpill mb-sm-3 mb-md-0 font-weight-500', {
                  active: tab === PAGE_TYPE.FINISHED_PROJECTS,
                })}
                style={{
                  fontSize: '14px',
                }}
                onClick={() => {
                  setTab(PAGE_TYPE.FINISHED_PROJECTS);
                }}
                href="#"
                role="tab"
              >
                <LuFolderCheck className="mr-2" size={18} />
                {translate('listWorkspaces.finishedSimulations')}
              </NavLink>
            </NavItem>
          </Nav>
        </div>
        {tab === PAGE_TYPE.WORKSPACES && (
          <Filter
            handleChange={handleChangeWorkspace}
            sortSelectedValue={sortSelectedValueWorkspace}
            sortOptions={sortOptionsWorkspace}
          />
        )}
        {tab === PAGE_TYPE.ACTIVE_PROJECTS && (
          <Filter
            handleChange={handleChange}
            sortSelectedValue={sortSelectedValue}
            sortOptions={sortOptions}
          />
        )}
        {tab === PAGE_TYPE.FINISHED_PROJECTS && (
          <Filter
            handleChange={handleChange}
            sortSelectedValue={sortSelectedValue}
            sortOptions={sortOptions}
          />
        )}
      </div>
    </div>
  );
}

interface IFilterProps {
  handleChange: (selectedOption: Option) => void;
  sortSelectedValue: Option;
  sortOptions: Option[];
}
const Filter = (props: IFilterProps) => {
  const { handleChange, sortSelectedValue, sortOptions } = props;

  return (
    <div
      className="ml-auto align-self-center justify-content-start custom-dropdown-align"
      style={{ width: '220px', zIndex: 1000 }}
    >
      <select
        className="custom-select custom-select-sm"
        value={sortSelectedValue.value}
        onChange={(e) => {
          const selectedOption = sortOptions.find(
            (option: Option) => option.value === e.target.value
          );
          handleChange(selectedOption ?? sortOptions[0]);
        }}
      >
        {sortOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default WorkspaceFilters;
